import {
  NOTIFICATION_FILTER_ALL,
  NOTIFICATION_FILTER_SET,
  NOTIFICATION_FILTER_UNREAD,
  NOTIFICATION_LIST_APPEND_SUCCESS,
  NOTIFICATION_LIST_FAIL,
  NOTIFICATION_LIST_REQUEST,
  NOTIFICATION_LIST_RESET,
  NOTIFICATION_LIST_SUCCESS,
  NOTIFICATION_MARK_ALL_READ_FAIL,
  NOTIFICATION_MARK_ALL_READ_REQUEST,
  NOTIFICATION_MARK_ALL_READ_SUCCESS,
  NOTIFICATION_MARK_READ_FAIL,
  NOTIFICATION_MARK_READ_REQUEST,
  NOTIFICATION_MARK_READ_SUCCESS,
  NOTIFICATION_READ,
  NOTIFICATION_RECEIVED,
  NOTIFICATION_UNREAD_COUNT_FAIL,
  NOTIFICATION_UNREAD_COUNT_REQUEST,
  NOTIFICATION_UNREAD_COUNT_SUCCESS,
} from "./notificationConstants";

const initialState = {
  items: [],
  unreadCount: 0,
  filter: NOTIFICATION_FILTER_ALL,
  page: 1,
  count: 0,
  next: null,
  previous: null,
  listGeneration: 0,
  pendingRealtimeOps: [],
};

const removeKey = (obj, key) => {
  if (!obj || obj[key] === undefined) return obj || {};
  const next = { ...obj };
  delete next[key];
  return next;
};

const patchItem = (items, id, patch) => {
  if (!Array.isArray(items)) return items;
  let changed = false;
  const next = items.map((item) => {
    if (!item || item.id !== id) return item;
    changed = true;
    return { ...item, ...patch };
  });
  return changed ? next : items;
};

const dedupeAppend = (existing, incoming) => {
  const seen = new Set((existing || []).map((item) => item?.id));
  const appended = [];
  for (const item of incoming || []) {
    if (!item || seen.has(item.id)) continue;
    seen.add(item.id);
    appended.push(item);
  }
  return [...(existing || []), ...appended];
};

const normalizeUnreadCount = (value) => {
  if (!Number.isSafeInteger(value) || value < 0) return null;
  return value;
};

const isValidNotificationId = (id) => Number.isSafeInteger(id) && id > 0;

const hasValidReadIds = (ids) => {
  if (ids === null) return true;
  if (!Array.isArray(ids)) return false;
  return ids.every((id) => isValidNotificationId(id));
};

/**
 * Pure transition for a realtime `notification` event against list/filter state.
 * Idempotent upsert by id; unreadCount is always the canonical server value.
 */
export const applyNotificationReceived = (state, notification, unreadCount) => {
  const canonicalUnread = normalizeUnreadCount(unreadCount);
  if (
    canonicalUnread == null
    || !notification
    || typeof notification !== "object"
    || Array.isArray(notification)
    || !isValidNotificationId(notification.id)
  ) {
    return state;
  }

  const items = Array.isArray(state.items) ? state.items : [];
  const filter = state.filter || NOTIFICATION_FILTER_ALL;
  const isUnreadFilter = filter === NOTIFICATION_FILTER_UNREAD;
  const existingIndex = items.findIndex((item) => item && item.id === notification.id);
  const isRead = Boolean(notification.is_read);

  if (existingIndex >= 0) {
    // chat.message must not remain in the center feed even if an older
    // client still held a row; drop it while accepting canonical unread.
    if (notification.type === "chat.message") {
      const nextItems = items.filter((item) => item && item.id !== notification.id);
      return {
        ...state,
        items: nextItems,
        unreadCount: canonicalUnread,
        count: isUnreadFilter
          ? canonicalUnread
          : Math.max(0, (Number(state.count) || 0) - (nextItems.length === items.length ? 0 : 1)),
      };
    }
    if (isUnreadFilter && isRead) {
      const nextItems = items.filter((item) => item && item.id !== notification.id);
      return {
        ...state,
        items: nextItems,
        unreadCount: canonicalUnread,
        count: canonicalUnread,
      };
    }
    const nextItems = items.slice();
    nextItems[existingIndex] = { ...items[existingIndex], ...notification };
    return {
      ...state,
      items: nextItems,
      unreadCount: canonicalUnread,
      count: isUnreadFilter ? canonicalUnread : state.count,
    };
  }

  // Genuinely new id.
  // Center feed excludes chat.message (durable for push/aggregation only).
  if (notification.type === "chat.message") {
    return {
      ...state,
      unreadCount: canonicalUnread,
      count: isUnreadFilter ? canonicalUnread : state.count,
    };
  }

  if (isUnreadFilter) {
    if (isRead) {
      return {
        ...state,
        unreadCount: canonicalUnread,
        count: canonicalUnread,
      };
    }
    return {
      ...state,
      items: [notification, ...items],
      unreadCount: canonicalUnread,
      count: canonicalUnread,
    };
  }

  return {
    ...state,
    items: [notification, ...items],
    unreadCount: canonicalUnread,
    count: (Number(state.count) || 0) + 1,
  };
};

/**
 * Pure transition for a realtime `notification_read` event.
 * `ids === null` means mark-all-read. `ids=[]` is a valid idempotent event.
 */
export const applyNotificationRead = (state, ids, unreadCount) => {
  const canonicalUnread = normalizeUnreadCount(unreadCount);
  if (canonicalUnread == null || !hasValidReadIds(ids)) {
    return state;
  }

  const items = Array.isArray(state.items) ? state.items : [];
  const filter = state.filter || NOTIFICATION_FILTER_ALL;
  const isUnreadFilter = filter === NOTIFICATION_FILTER_UNREAD;

  if (ids === null) {
    if (isUnreadFilter) {
      return {
        ...state,
        items: [],
        count: 0,
        next: null,
        previous: null,
        page: 1,
        unreadCount: canonicalUnread,
      };
    }
    return {
      ...state,
      unreadCount: canonicalUnread,
      items: items.map((item) =>
        item && !item.is_read
          ? { ...item, is_read: true, read_at: item.read_at || new Date().toISOString() }
          : item,
      ),
    };
  }

  const idSet = new Set(ids);
  if (isUnreadFilter) {
    const nextItems = items.filter((item) => !item || !idSet.has(item.id));
    return {
      ...state,
      items: nextItems,
      unreadCount: canonicalUnread,
      count: canonicalUnread,
    };
  }

  return {
    ...state,
    unreadCount: canonicalUnread,
    items: items.map((item) =>
      item && idSet.has(item.id) && !item.is_read
        ? { ...item, is_read: true, read_at: item.read_at || new Date().toISOString() }
        : item,
    ),
  };
};

const appendPendingOp = (state, op) => [
  ...(Array.isArray(state.pendingRealtimeOps) ? state.pendingRealtimeOps : []),
  op,
];

const replayPendingRealtimeOps = (state, ops) => {
  let next = state;
  for (const op of ops || []) {
    if (!op) continue;
    if (op.type === "received") {
      next = applyNotificationReceived(next, op.notification, op.unreadCount);
    } else if (op.type === "read") {
      next = applyNotificationRead(next, op.ids, op.unreadCount);
    }
  }
  return next;
};

export const notificationReducer = (state = initialState, action) => {
  switch (action.type) {
    case NOTIFICATION_LIST_REQUEST:
      if (action.meta?.append) {
        return {
          ...state,
          loadingMore: true,
          loadMoreError: undefined,
        };
      }
      return {
        ...state,
        loading: true,
        error: undefined,
        filter: action.meta?.filter ?? state.filter,
        listGeneration: action.meta?.generation ?? state.listGeneration,
        // Buffer realtime ops that arrive while this list request is in flight.
        pendingRealtimeOps: [],
      };

    case NOTIFICATION_LIST_SUCCESS: {
      if (
        action.meta?.generation != null &&
        action.meta.generation !== state.listGeneration
      ) {
        return state;
      }
      const base = {
        ...state,
        loading: false,
        loadingMore: false,
        success: true,
        items: action.payload?.items ?? [],
        count: action.payload?.count ?? 0,
        next: action.payload?.next ?? null,
        previous: action.payload?.previous ?? null,
        page: 1,
        filter: action.meta?.filter ?? state.filter,
        error: undefined,
        loadMoreError: undefined,
      };
      const pending = Array.isArray(state.pendingRealtimeOps)
        ? state.pendingRealtimeOps
        : [];
      const merged = replayPendingRealtimeOps(base, pending);
      return {
        ...merged,
        pendingRealtimeOps: [],
      };
    }

    case NOTIFICATION_LIST_APPEND_SUCCESS:
      if (
        action.meta?.generation != null &&
        action.meta.generation !== state.listGeneration
      ) {
        return {
          ...state,
          loadingMore: false,
        };
      }
      return {
        ...state,
        loadingMore: false,
        success: true,
        items: dedupeAppend(state.items, action.payload?.items ?? []),
        count: action.payload?.count ?? state.count,
        next: action.payload?.next ?? null,
        previous: action.payload?.previous ?? null,
        page: action.meta?.page ?? state.page,
        loadMoreError: undefined,
      };

    case NOTIFICATION_LIST_FAIL:
      if (
        action.meta?.generation != null &&
        action.meta.generation !== state.listGeneration
      ) {
        // Stale generation must not mutate newer request ownership/buffer/loading.
        return state;
      }
      if (action.meta?.append) {
        return {
          ...state,
          loadingMore: false,
          loadMoreError: action.payload,
        };
      }
      return {
        ...state,
        loading: false,
        error: action.payload,
        // Matching non-append failure: keep live-applied realtime rows, drop buffer.
        pendingRealtimeOps: [],
      };

    case NOTIFICATION_LIST_RESET:
      return initialState;

    case NOTIFICATION_FILTER_SET:
      return {
        ...state,
        filter: action.payload,
        page: 1,
        next: null,
        previous: null,
      };

    case NOTIFICATION_MARK_READ_REQUEST:
      return {
        ...state,
        markingIds: {
          ...(state.markingIds || {}),
          [action.meta.id]: true,
        },
        markReadError: undefined,
      };

    case NOTIFICATION_MARK_READ_SUCCESS: {
      const priorItem = Array.isArray(state.items)
        ? state.items.find((item) => item && item.id === action.meta.id)
        : null;
      const wasUnread = Boolean(priorItem && !priorItem.is_read);
      const confirmedRead = action.payload?.is_read !== false;
      const shouldDecrement = wasUnread && confirmedRead;
      const isUnreadFilter = state.filter === NOTIFICATION_FILTER_UNREAD;
      return {
        ...state,
        items: isUnreadFilter
          ? state.items
          : patchItem(state.items, action.meta.id, action.payload || {}),
        markingIds: removeKey(state.markingIds, action.meta.id),
        markReadError: undefined,
        unreadCount: shouldDecrement
          ? Math.max(0, (state.unreadCount || 0) - 1)
          : state.unreadCount,
      };
    }

    case NOTIFICATION_MARK_READ_FAIL:
      return {
        ...state,
        markingIds: removeKey(state.markingIds, action.meta.id),
        markReadError: { id: action.meta.id, message: action.payload },
      };

    case NOTIFICATION_MARK_ALL_READ_REQUEST:
      return {
        ...state,
        markAllLoading: true,
        markAllError: undefined,
      };

    case NOTIFICATION_MARK_ALL_READ_SUCCESS:
      if (state.filter === NOTIFICATION_FILTER_UNREAD) {
        return {
          ...state,
          markAllLoading: false,
          markAllError: undefined,
          unreadCount: 0,
          items: [],
          count: 0,
          next: null,
          previous: null,
          page: 1,
        };
      }
      return {
        ...state,
        markAllLoading: false,
        markAllError: undefined,
        unreadCount: 0,
        items: (state.items || []).map((item) =>
          item && !item.is_read
            ? { ...item, is_read: true, read_at: item.read_at || new Date().toISOString() }
            : item,
        ),
      };

    case NOTIFICATION_MARK_ALL_READ_FAIL:
      return {
        ...state,
        markAllLoading: false,
        markAllError: action.payload,
      };

    case NOTIFICATION_UNREAD_COUNT_REQUEST:
      return { ...state, unreadCountLoading: true };

    case NOTIFICATION_UNREAD_COUNT_SUCCESS:
      return {
        ...state,
        unreadCountLoading: false,
        unreadCount: Number(action.payload) || 0,
      };

    case NOTIFICATION_UNREAD_COUNT_FAIL:
      return { ...state, unreadCountLoading: false };

    case NOTIFICATION_RECEIVED: {
      const notification = action.payload?.notification;
      const unreadCount = action.payload?.unreadCount;
      if (
        !notification
        || typeof notification !== "object"
        || Array.isArray(notification)
        || !isValidNotificationId(notification.id)
        || normalizeUnreadCount(unreadCount) == null
      ) {
        return state;
      }
      const next = applyNotificationReceived(state, notification, unreadCount);
      if (next === state) {
        return state;
      }
      // Buffer only while an active non-append list request is in flight.
      if (state.loading === true) {
        return {
          ...next,
          pendingRealtimeOps: appendPendingOp(state, {
            type: "received",
            notification,
            unreadCount,
          }),
        };
      }
      return next;
    }

    case NOTIFICATION_READ: {
      const ids = action.payload?.ids;
      const unreadCount = action.payload?.unreadCount;
      if (normalizeUnreadCount(unreadCount) == null || !hasValidReadIds(ids)) {
        return state;
      }
      const next = applyNotificationRead(state, ids, unreadCount);
      if (next === state) {
        return state;
      }
      if (state.loading === true) {
        return {
          ...next,
          pendingRealtimeOps: appendPendingOp(state, {
            type: "read",
            ids,
            unreadCount,
          }),
        };
      }
      return next;
    }

    default:
      return state;
  }
};

export default notificationReducer;
